import { useId } from 'react';
import {
  type Control,
  type FieldArrayWithId,
  type FieldErrors,
  type Path,
} from 'react-hook-form';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import HelperTextContainer from '@components/HelperTextContainer';
import FileBrowserTextField from '@components/FormFields/FileBrowserTextField';
import FormSelect from '@components/FormFields/FormSelect';
import { type GroupExperimentSchema } from '@/zod_schemas';

interface IFilesContainer2Props {
  control: Control<GroupExperimentSchema>;
  errors: FieldErrors<GroupExperimentSchema>;
  sampleIndex: number;
  fields: FieldArrayWithId<GroupExperimentSchema>[];
}

const FilesContainer2 = ({
  sampleIndex,
  control,
  errors,
  fields,
}: IFilesContainer2Props) => {
  const filesPath: Path<GroupExperimentSchema> = `samples.${sampleIndex}.files`;
  const fileErrors = errors.samples?.[sampleIndex]?.files;
  const fieldIdPrefix = useId();

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
        Input files
      </Typography>
      <Stack spacing={0} divider={<Divider flexItem />}>
        {fields.map(({ id }, fileIndex) => (
          <Box
            key={id}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'minmax(10rem, 1fr) minmax(0, 3fr)',
              },
              columnGap: 1.5,
              rowGap: 1.25,
              py: 1.25,
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel
                id={`${fieldIdPrefix}-file-type-${fileIndex}-label`}
                htmlFor={`${fieldIdPrefix}-file-type-${fileIndex}`}
              >
                File {fileIndex + 1} type
              </InputLabel>
              <FormSelect
                controllerProps={{
                  control,
                  name: `${filesPath}.${fileIndex}.file_type`,
                }}
                selectProps={{
                  labelId: `${fieldIdPrefix}-file-type-${fileIndex}-label`,
                  id: `${fieldIdPrefix}-file-type-${fileIndex}`,
                  label: `File ${fileIndex + 1} type`,
                  'aria-describedby': `${fieldIdPrefix}-file-type-${fileIndex}-helper-text`,
                }}
              >
                <MenuItem value="fastq_forward">FASTQ Forward</MenuItem>
                <MenuItem value="fastq_reverse">FASTQ Reverse</MenuItem>
                <MenuItem value="bam">BAM</MenuItem>
              </FormSelect>
              <HelperTextContainer
                id={`${fieldIdPrefix}-file-type-${fileIndex}-helper-text`}
              >
                <span>{fileErrors?.[fileIndex]?.file_type?.message}</span>
              </HelperTextContainer>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel
                htmlFor={`${fieldIdPrefix}-file-${fileIndex}`}
                id={`${fieldIdPrefix}-file-${fileIndex}-label`}
              >
                File {fileIndex + 1} path
              </InputLabel>
              <FileBrowserTextField
                variant="outlined"
                label={`File ${fileIndex + 1} path`}
                inputProps={{
                  id: `${fieldIdPrefix}-file-${fileIndex}`,
                  type: 'text',
                  size: 'small',
                  fullWidth: true,
                  placeholder: 'Select a file',
                  'aria-describedby': `${fieldIdPrefix}-file-${fileIndex}-helper-text`,
                }}
                controllerProps={{
                  control,
                  name: `${filesPath}.${fileIndex}.file_path`,
                }}
              />
              <HelperTextContainer
                id={`${fieldIdPrefix}-file-${fileIndex}-helper-text`}
              >
                <span>{fileErrors?.[fileIndex]?.file_path?.message}</span>
              </HelperTextContainer>
            </FormControl>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

export default FilesContainer2;
